import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { SEO_PAGES, SITE_URL } from "../../constants/seo";
import { ROUTES } from "../../constants/routes";
import { SITE, SOCIAL_LINKS } from "../../constants/site";
import { useProductSeo } from "../../context/ProductSeoContext";
import { buildAbsoluteUrl } from "../../utils/seo";

function JsonLd({ pageId = "home" }) {
  const { t } = useTranslation();
  const { productSeo } = useProductSeo();
  const page = SEO_PAGES[pageId] || SEO_PAGES.home;
  const pageUrl =
    pageId === "productDetail" && productSeo?.slug
      ? buildAbsoluteUrl(`${ROUTES.products}/${productSeo.slug}`)
      : buildAbsoluteUrl(page.path);
  const logoUrl = buildAbsoluteUrl("/logoDark.svg");

  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    url: SITE_URL,
    logo: logoUrl,
    email: SITE.emails.info,
    telephone: SITE.phones.egyptPrimary,
    sameAs: SOCIAL_LINKS.filter((link) => link.external).map((link) => link.href),
    areaServed: ["EG", "AE"],
    description: t("seo.home.description"),
  };

  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE_URL,
    inLanguage: ["en", "ar"],
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: logoUrl,
    },
  };

  const breadcrumbItems = [
    {
      "@type": "ListItem",
      position: 1,
      name: t("Home"),
      item: buildAbsoluteUrl(SEO_PAGES.home.path),
    },
  ];

  if (pageId !== "home") {
    breadcrumbItems.push({
      "@type": "ListItem",
      position: 2,
      name: t(page.breadcrumbKey),
      item:
        pageId === "productDetail"
          ? buildAbsoluteUrl(ROUTES.products)
          : pageUrl,
    });
  }

  if (pageId === "productDetail" && productSeo?.name) {
    breadcrumbItems.push({
      "@type": "ListItem",
      position: 3,
      name: productSeo.name,
      item: pageUrl,
    });
  }

  const breadcrumb =
    pageId === "home"
      ? null
      : {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbItems,
        };

  const productSchema =
    pageId === "productDetail" && productSeo?.name
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: productSeo.name,
          description: productSeo.description,
          image: productSeo.image ? [productSeo.image] : undefined,
          sku: productSeo.sku || undefined,
          url: pageUrl,
          brand: {
            "@type": "Brand",
            name: SITE.name,
          },
          offers: productSeo.price
            ? {
                "@type": "Offer",
                price: productSeo.price,
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
                url: pageUrl,
              }
            : undefined,
        }
      : null;

  const localBusiness =
    pageId === "contact"
      ? [
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: `${SITE.name} — UAE`,
            url: pageUrl,
            email: SITE.emails.uae,
            telephone: SITE.phones.uae,
            address: {
              "@type": "PostalAddress",
              addressCountry: "AE",
              addressRegion: "United Arab Emirates",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: `${SITE.name} — Egypt`,
            url: pageUrl,
            email: SITE.emails.egypt,
            telephone: SITE.phones.egyptPrimary,
            address: {
              "@type": "PostalAddress",
              addressCountry: "EG",
              addressLocality: "Nasr City, Cairo",
              streetAddress: "Medical Center 3",
            },
          },
        ]
      : [];

  const schemas = [
    organization,
    website,
    breadcrumb,
    productSchema,
    ...localBusiness,
  ].filter(Boolean);

  const jsonLd =
    schemas.length === 1
      ? schemas[0]
      : {
          "@context": "https://schema.org",
          "@graph": schemas.map((schema) => {
            const { "@context": _context, ...rest } = schema;
            return rest;
          }),
        };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}

export default JsonLd;
